import { StyleSheet, View } from 'react-native'
import { Card, Icon, Text, useTheme } from 'react-native-paper'
import { useActiveEntry } from '@/hooks/useActiveEntry'
import { useClockIn } from '@/hooks/useClockIn'
import { Button } from '@/ui/Button'
import { TextAreaInput, useForm, z, zodResolver } from '@/ui/form'
import { JobPicker } from '@/ui/time-entry/JobPicker'
import { LocationPicker } from '@/ui/time-entry/LocationPicker'

const clockInSchema = z.object({
    jobId: z.string().min(1, 'Job is required'),
    locationId: z.string().optional(),
    notes: z.string().trim().min(3, "Add a short note about what you're working on"),
})

type ClockInFormData = z.infer<typeof clockInSchema>

interface ClockInFormProps {
    orgSlug: string
    isLocked?: boolean
}

export function ClockInForm({ orgSlug, isLocked }: ClockInFormProps) {
    const theme = useTheme()
    const clockInMutation = useClockIn()
    const { activeEntry, activeBreakEntry } = useActiveEntry()

    const {
        control,
        handleSubmit,
        formState: { isValid },
        reset,
        watch,
    } = useForm<ClockInFormData>({
        resolver: zodResolver(clockInSchema),
        defaultValues: {
            jobId: '',
            locationId: '',
            notes: '',
        },
        mode: 'onChange',
    })

    const jobId = watch('jobId')

    const onSubmit = async (data: ClockInFormData) => {
        try {
            await clockInMutation.mutateAsync({
                jobId: data.jobId,
                locationId: data.locationId || undefined,
                notes: data.notes || undefined,
                activeEntryToClose: activeEntry
                    ? { id: activeEntry.id, clock_in: activeEntry.clock_in }
                    : undefined,
                activeBreakToClose: activeBreakEntry
                    ? { id: activeBreakEntry.id, clock_in: activeBreakEntry.clock_in }
                    : undefined,
            })
            reset()
        } catch (_error) {
            // Error is already captured in the mutation hook
        }
    }

    if (isLocked) {
        return (
            <Card style={styles.card}>
                <Card.Content>
                    <View style={styles.lockedContainer}>
                        <Icon source="lock" size={24} color={theme.colors.onSurfaceVariant} />
                        <Text
                            variant="titleMedium"
                            style={{ color: theme.colors.onSurfaceVariant }}
                        >
                            Period is locked
                        </Text>
                        <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                            This period has been locked by an admin. Contact your admin if you need
                            to make changes.
                        </Text>
                    </View>
                </Card.Content>
            </Card>
        )
    }

    return (
        <Card style={styles.card}>
            <Card.Content>
                <Text
                    variant="bodyMedium"
                    style={[styles.subtitle, { color: theme.colors.onSurfaceVariant }]}
                >
                    Start tracking your time
                </Text>

                <View style={styles.form}>
                    <JobPicker name="jobId" control={control} orgSlug={orgSlug} />
                    {jobId ? (
                        <LocationPicker name="locationId" control={control} jobId={jobId} />
                    ) : null}
                    <TextAreaInput
                        name="notes"
                        control={control}
                        label="What are you working on?"
                        placeholder="e.g. Framing south wall · Electrical rough-in · Punch list · Equipment maintenance"
                        numberOfLines={3}
                    />
                    <Button
                        title="Clock In"
                        testID="clock-in-submit"
                        mode="contained"
                        onPress={handleSubmit(onSubmit)}
                        disabled={!isValid || clockInMutation.isPending}
                        loading={clockInMutation.isPending}
                        icon="clock-start"
                        style={styles.button}
                    />
                </View>
            </Card.Content>
        </Card>
    )
}

const styles = StyleSheet.create({
    card: {
        elevation: 2,
    },
    subtitle: {
        marginBottom: 24,
    },
    form: {
        gap: 16,
    },
    button: {
        marginTop: 8,
        alignSelf: 'flex-end',
    },
    lockedContainer: {
        alignItems: 'center',
        gap: 8,
        paddingVertical: 16,
    },
})
